import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "@helpers/translate";
import "./index.sass";

const CryptoMarketCapOverview = ({ chartConfig }) => {
  const { __ } = useTranslation();
  const primarySeries = chartConfig?.series?.[0];
  const [activePoint, setActivePoint] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState(null);

  const handleMouseMove = (event) => {
    const pointsData = primarySeries?.pointsData || [];

    if (!pointsData.length) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const mouseX =
      ((event.clientX - rect.left) / rect.width) * chartConfig.chartWidth;

    const nearestPoint = pointsData.reduce((closestPoint, point) => {
      const closestDistance = Math.abs(closestPoint.x - mouseX);
      const currentDistance = Math.abs(point.x - mouseX);

      return currentDistance < closestDistance ? point : closestPoint;
    }, pointsData[0]);

    setActivePoint(nearestPoint);
    setTooltipPosition({
      left: event.clientX,
      top: event.clientY,
    });
  };

  const handleMouseLeave = () => {
    setActivePoint(null);
    setTooltipPosition(null);
  };

  return (
    <div className="crypto-market-cap-overview">
      <div className="crypto-market-cap-overview__y-axis">
        {chartConfig?.yAxisLabels?.map((label) => (
          <span key={label} className="crypto-market-cap-overview__y-label">
            {label}
          </span>
        ))}
      </div>

      <div className="crypto-market-cap-overview__chart-container">
        <svg
          viewBox={`0 0 ${chartConfig?.chartWidth || 500} ${
            chartConfig?.chartHeight || 200
          }`}
          preserveAspectRatio="none"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <defs>
            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.35" />
              <stop offset="50%" stopColor="#10b981" stopOpacity="0.06" />
              <stop offset="100%" stopOpacity="0" />
            </linearGradient>
          </defs>

          <polygon
            points={primarySeries?.areaPoints || ""}
            fill="url(#areaGradient)"
          />

          <polyline
            points={primarySeries?.points || ""}
            fill="none"
            stroke="#10b981"
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />

          {activePoint && (
            <>
              <line
                x1={activePoint.x}
                y1="0"
                x2={activePoint.x}
                y2={chartConfig?.chartHeight || 200}
                stroke="rgba(255, 255, 255, 0.3)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />

              <ellipse
                cx={activePoint.x}
                cy={activePoint.y}
                rx="1"
                ry="3"
                fill="#10b981"
                stroke="#ffffff"
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
              />
            </>
          )}
        </svg>

        <div className="crypto-market-cap-overview__x-axis">
          {chartConfig?.xAxisLabels?.map((label, index) => (
            <span
              key={`${label}-${index}`}
              className="crypto-market-cap-overview__x-label"
            >
              {label}
            </span>
          ))}
        </div>
      </div>

      {activePoint &&
        tooltipPosition &&
        createPortal(
          <div
            className="crypto-market-cap-overview__tooltip"
            style={{
              left: tooltipPosition.left,
              top: tooltipPosition.top,
            }}
          >
            <span className="crypto-market-cap-overview__tooltip-date">
              {activePoint.tooltipDate}
            </span>

            <span className="crypto-market-cap-overview__tooltip-row">
              {__("markets.cryptoMarketCapMarketCap")}: {activePoint.marketCap}
            </span>

            <span className="crypto-market-cap-overview__tooltip-row">
              {__("markets.cryptoMarketCapVolume")}: {activePoint.volume}
            </span>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default CryptoMarketCapOverview;
